import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { MONGODB_AWSGW_QUERIES, MONGODB_AWSGW_APIKEY } from "../../utility/keys";
import { parseCwLogEvent } from "../../utility/cwLogParse";
import { windowMs } from "./logSources";
import { LEVEL_STATUS, LEVEL_ORDER } from "../../utility/logAnalytics";
import LogsErrorBoundary from "./LogsErrorBoundary";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// LogChartParts (#247) — shared pieces for the Log viewer's analytics modals.
// A modal-shell, two Recharts charts (status-colored, single count axis, legend,
// tooltip), and the CloudWatch data fetcher, which only runs its hooks while the
// modal is open (the modal renders it conditionally). Keeps both chart modals
// DRY and consistent.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export function ChartModalShell({ title, subtitle, onClose, children }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 9998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        className="bg-skin-primary text-skin-base border-2 border-skin-base rounded-md p-4"
        style={{ width: "80vw", height: "72vh", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-semibold">{title}</div>
            {subtitle && <div className="text-xs text-skin-link">{subtitle}</div>}
          </div>
          <button
            className="px-3 py-1 border-2 border-skin-base rounded-sm hover:bg-skin-base-hover"
            onClick={onClose}
          >
            Close ✕
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <LogsErrorBoundary label="this chart">{children}</LogsErrorBoundary>
        </div>
      </div>
    </div>
  );
}

// Stacked bars: count per time bucket, stacked by status level. One y-axis.
export function StackedLevelBars({ data }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.25)" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
        <Tooltip
          contentStyle={{ fontSize: 12 }}
          labelStyle={{ color: "#111" }}
          itemStyle={{ color: "#111" }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {LEVEL_ORDER.map((lvl, i) => (
          <Bar
            key={lvl}
            dataKey={lvl}
            name={LEVEL_STATUS[lvl].label}
            stackId="lvl"
            fill={LEVEL_STATUS[lvl].color}
            radius={i === LEVEL_ORDER.length - 1 ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// Horizontal top-N bars (single hue = magnitude). data: [{ key, count }].
export function TopBars({ data, color = "#2563eb" }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.25)" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="key" width={200} tick={{ fontSize: 11 }} />
        <Tooltip contentStyle={{ fontSize: 12 }} itemStyle={{ color: "#111" }} />
        <Bar dataKey="count" name="Count" fill={color} radius={[0, 4, 4, 0]}>
          {data.map((_, i) => (
            <Cell key={i} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// - - - data fetchers (only mounted while a modal is open) - - -

// CloudWatch events over a window. Calls render(events, loading, truncated, error).
//
// ⚠️ WHY THIS PAGES INSTEAD OF TAKING ONE BIG PAGE. FilterLogEvents returns
// matches in ASCENDING time order from startTime, so a single `limit: 1000`
// fetch on a busy group returns the OLDEST 1000 events in the window, not the
// newest. Measured against the deployed proxy: a 6-hour window returned 50
// events all sitting at minute 13 of 360, with a nextToken behind them. The
// first cut of this chart did exactly that AND labelled it "the most recent
// 1,000 events" — so every bar would have crowded into the left edge while the
// operator was told they were looking at current activity. A chart that is
// wrong in a plausible-looking way is worse than one that is obviously empty.
//
// So: follow nextToken to the end of the window, up to a bounded number of
// pages. If the cap is hit we hold the OLDEST events and the newest are still
// unfetched — the caller is told so in those words, not the reverse.
const CHART_PAGE_LIMIT = 1000;
const CHART_MAX_PAGES = 8; // 8k events — enough for any real window, bounded cost

export function CwEvents({ groupId, windowId, anchorMs, render }) {
  const startTime = anchorMs - windowMs(windowId);
  const { data, isFetching } = useQuery({
    queryKey: ["cwLogsChart", groupId, windowId, anchorMs],
    queryFn: async () => {
      const all = [];
      let token = null;
      let pages = 0;
      let serverError = null;
      do {
        const resp = await fetch(MONGODB_AWSGW_QUERIES, {
          method: "POST",
          headers: { "x-api-key": MONGODB_AWSGW_APIKEY, "Content-Type": "application/json" },
          body: JSON.stringify({
            method: "cwLogs",
            groupId,
            startTime,
            endTime: anchorMs,
            filterPattern: "",
            limit: CHART_PAGE_LIMIT,
            ...(token ? { nextToken: token } : {}),
          }),
        });
        const json = await resp.json();
        const result = json?.message?.result;
        if (!result) break;
        if (result.error) {
          serverError = result.error;
          break;
        }
        all.push(...(result.events || []));
        token = result.nextToken ?? null;
        pages += 1;
      } while (token && pages < CHART_MAX_PAGES);
      // `truncated` = we stopped early, so the NEWEST events are missing.
      return { events: all, truncated: Boolean(token), error: serverError };
    },
  });
  const events = useMemo(
    () => (data?.events || []).map((e) => parseCwLogEvent(e)),
    [data]
  );
  return render(events, isFetching, Boolean(data?.truncated), data?.error || null);
}
